/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

import { getAuthHeaders, getAuthToken, getAuthTokenExpiryMs, removeAuthToken, setAuthToken } from './auth';
import { showNotification } from '@mantine/notifications';

// Use environment variable injected at build time (Create-React-App)
// Fallback to localhost when not provided, e.g. during local dev without Docker
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Validate API_BASE_URL is set
if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn('REACT_APP_API_BASE_URL is not set, using default:', API_BASE_URL);
}

const CONNECTION_TOAST_COOLDOWN_MS = 45000;
const RETRY_DELAYS_MS = [400, 1200];
let lastConnectionToastAt = 0;
let refreshPromise: Promise<boolean> | null = null;
let unauthorizedHandled = false;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const parseResponseBody = async (response: Response) => {
  const contentType = response.headers.get('content-type');
  const text = await response.text();

  if (!text || text.trim() === '') {
    return null;
  }

  if (contentType && contentType.includes('application/json')) {
    return JSON.parse(text);
  }

  return text;
};

const showConnectionErrorNotification = (message: string) => {
  const now = Date.now();
  if (now - lastConnectionToastAt < CONNECTION_TOAST_COOLDOWN_MS) {
    return;
  }

  lastConnectionToastAt = now;
  showNotification({
    title: 'Connection Error',
    message,
    color: 'red',
  });
};

const getNetworkErrorMessage = () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You appear to be offline. Please check your internet connection and try again.';
  }
  return `Unable to connect to server. Please check if the backend is running at ${API_BASE_URL}`;
};

const isNetworkError = (error: any) =>
  error?.message === 'Failed to fetch' ||
  error?.name === 'TypeError' ||
  error?.name === 'NetworkError';

const fetchWithRetry = async (
  url: string,
  init: RequestInit,
  options?: { retryOnNetworkError?: boolean; timeoutMs?: number },
): Promise<Response> => {
  const retryOnNetworkError = options?.retryOnNetworkError ?? false;
  const attempts = retryOnNetworkError ? RETRY_DELAYS_MS.length + 1 : 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), options?.timeoutMs ?? 30000);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new Error('Request timed out. An action may still be running; refresh its status before retrying.');
      }
      const canRetry = retryOnNetworkError && isNetworkError(error) && attempt < attempts - 1;
      if (!canRetry) {
        throw error;
      }
      await sleep(RETRY_DELAYS_MS[attempt]);
    } finally {
      window.clearTimeout(timer);
    }
  }

  throw new Error('Network request failed');
};

const handleUnauthorized = () => {
  if (unauthorizedHandled) return;
  unauthorizedHandled = true;
  removeAuthToken();
  localStorage.removeItem('user');

  showNotification({
    title: 'Session Expired',
    message: 'Your credentials are not validated. Please sign in again.',
    color: 'red',
  });

  setTimeout(() => {
    if (!getAuthToken()) window.location.href = '/login';
    unauthorizedHandled = false;
  }, 1000);
};

const refreshSession = (): Promise<boolean> => {
  if (!refreshPromise) {
    const tokenAtStart = getAuthToken();
    refreshPromise = fetchWithRetry(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST', credentials: 'include',
    }).then(async (response) => {
      if (response.status === 401 || response.status === 403) return false;
      if (!response.ok) throw new Error('Session renewal is temporarily unavailable. Please try again.');
      const data = await response.json();
      if (!data?.access_token) throw new Error('Session renewal returned no access token.');
      if (!getAuthToken()) return false;
      if (getAuthToken() !== tokenAtStart) return true;
      setAuthToken(data.access_token);
      unauthorizedHandled = false;
      return true;
    }).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

const withCurrentAuth = (init: RequestInit): RequestInit => ({
  ...init,
  headers: { ...(init.headers as Record<string, string>), ...getAuthHeaders() },
});

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const getErrorFromResponse = async (response: Response): Promise<{ message: string; data: any }> => {
  let errorMessage = `Request failed with status ${response.status}`;
  let responseData: any = null;
  const responseClone = response.clone();
  try {
    const errorData = await response.json();
    responseData = errorData;
    const detail = errorData?.detail || errorData?.message;
    errorMessage = typeof detail === 'string' ? detail : Array.isArray(detail)
      ? detail.map((item: any) => item.msg || 'Invalid request').join('; ')
      : typeof detail?.message === 'string' ? detail.message : errorMessage;
  } catch {
    try {
      const text = await responseClone.text();
      if (text) {
        errorMessage = text;
        responseData = text;
      }
    } catch {
      // Ignore parsing errors
    }
  }
  return { message: errorMessage, data: responseData };
};

const request = async (
  endpoint: string,
  init: RequestInit,
  options?: {
    retryOnNetworkError?: boolean;
    handleUnauthorized?: boolean;
    silent?: boolean;
    timeoutMs?: number;
  },
) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const silent = options?.silent ?? false;
  const authenticated = options?.handleUnauthorized !== false && !!getAuthToken();
  try {
    const expiry = authenticated ? getAuthTokenExpiryMs() : null;
    if (expiry !== null && expiry <= Date.now() + 60000) {
      if (!(await refreshSession())) {
        handleUnauthorized();
        throw new ApiError('Session expired. Please sign in again.', 401, null);
      }
    }
    const sentToken = getAuthToken();
    let response = await fetchWithRetry(url, authenticated ? withCurrentAuth(init) : init, {
      retryOnNetworkError: options?.retryOnNetworkError ?? false,
      timeoutMs: options?.timeoutMs,
    });

    if (response.status === 401 && authenticated) {
      // A successful refresh is shared by concurrent requests. Only GET is
      // replayed; a trading mutation must be deliberately retried by its user.
      const renewed = getAuthToken() !== sentToken || await refreshSession();
      if (!renewed) {
        handleUnauthorized();
        throw new ApiError('Session expired. Please sign in again.', 401, null);
      }
      if (init.method === 'GET') {
        response = await fetchWithRetry(url, withCurrentAuth(init), {
          retryOnNetworkError: options?.retryOnNetworkError ?? false,
        });
      } else {
        throw new ApiError('Session renewed. Check the action status before trying again.', 401, null);
      }
    }

    if (!response.ok) {
      const parsedError = await getErrorFromResponse(response);
      if (!silent) {
        showNotification({
          title: 'Error',
          message: parsedError.message,
          color: 'red',
        });
      }
      throw new ApiError(parsedError.message, response.status, parsedError.data);
    }

    return parseResponseBody(response);
  } catch (error: any) {
    if (isNetworkError(error)) {
      const errorMsg = getNetworkErrorMessage();
      if (!silent) {
        showConnectionErrorNotification(errorMsg);
      }
      throw new Error(errorMsg);
    }
    throw error;
  }
};

/**
 * API client with authentication
 */
export const apiClient = {
  /**
   * GET request with authentication.
   *
   * Pass `{ silent: true }` to suppress error/connection toasts for
   * background/bootstrap calls (e.g. `/public-config`) where the SPA
   * should fall back to defaults instead of nagging the user.
   */
  get: async (endpoint: string, options?: { silent?: boolean }) => {
    const publicEndpoint = endpoint === '/public-config' || endpoint === '/health' || endpoint === '/version';
    return request(
      endpoint,
      {
        method: 'GET',
        headers: publicEndpoint ? {} : getAuthHeaders(),
      },
      {
        retryOnNetworkError: true,
        handleUnauthorized: !publicEndpoint,
        silent: options?.silent ?? false,
      },
    );
  },

  /**
   * POST request with authentication.
   *
   * Pass `{ silent: true }` for background probes (e.g. wallet balances) where
   * the caller handles failure and a global error toast would be noisy.
   */
  post: async (endpoint: string, data: any, options?: { silent?: boolean; timeoutMs?: number }) => {
    return request(
      endpoint,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
      {
        retryOnNetworkError: false,
        handleUnauthorized: true,
        silent: options?.silent ?? false,
        // Only explicitly requested long operator checks extend the default.
        timeoutMs: options?.timeoutMs,
      },
    );
  },

  /**
   * PUT request with authentication
   */
  put: async (endpoint: string, data: any) => {
    return request(
      endpoint,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
      { retryOnNetworkError: false, handleUnauthorized: true },
    );
  },

  /**
   * PATCH request with authentication
   */
  patch: async (endpoint: string, data: any) => {
    return request(
      endpoint,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
      { retryOnNetworkError: false, handleUnauthorized: true },
    );
  },

  /**
   * DELETE request with authentication
   */
  delete: async (endpoint: string) => {
    return request(
      endpoint,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      },
      { retryOnNetworkError: false, handleUnauthorized: true },
    );
  },
};

export const fetchBots = async () => {
  try {
    // Fetch both regular DCA bots and per-user grid bots
    const [regularBots, gridBots] = await Promise.all([
      apiClient.get('/bots/get').catch(() => []),
      apiClient.get('/api/grid-bots').catch(() => [])
    ]);
    
    // Map grid bot state to display status; when paused show "paused"
    const gridStateToStatus = (state: string | undefined, isActive: boolean): string => {
      if (isActive === false) return 'paused';
      if (!state) return 'active';
      return state
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    // Combine bots and mark grid bots with status for card display
    const allBots = [
      ...(Array.isArray(regularBots) ? regularBots : []),
      ...(Array.isArray(gridBots)
        ? gridBots.map((bot: any) => ({
            ...bot,
            bot_type: 'grid',
            status: bot.status ?? gridStateToStatus(bot.state, bot.is_active),
          }))
        : []),
    ];
    
    return allBots;
  } catch (error) {
    console.error('Error fetching bots:', error);
    // Don't show additional notification here since apiClient already handles it
    throw error;
  }
};

export const pauseBot = async (botId: number) => {
  try {
    const data = await apiClient.put(`/bots/${botId}/pause`, {});

    // Show success notification
    showNotification({
      title: 'Bot Paused',
      message: `Bot #${botId} has been paused successfully`,
      color: 'green',
    });

    return data;
  } catch (error) {
    console.error(`Error pausing bot ${botId}:`, error);
    // Don't show additional notification here since apiClient already handles it
    throw error;
  }
};

export const resumeBot = async (botId: number) => {
  try {
    const data = await apiClient.put(`/bots/${botId}/resume`, {});

    // Show success notification
    showNotification({
      title: 'Bot Resumed',
      message: `Bot #${botId} has been resumed successfully`,
      color: 'green',
    });

    return data;
  } catch (error) {
    console.error(`Error resuming bot ${botId}:`, error);
    // Don't show additional notification here since apiClient already handles it
    throw error;
  }
};

export const deleteBot = async (botId: number) => {
  try {
    const data = await apiClient.delete(`/bots/${botId}`);

    // Show success notification
    showNotification({
      title: 'Bot Deleted',
      message: `Bot #${botId} has been deleted successfully`,
      color: 'green',
    });

    return data;
  } catch (error) {
    console.error(`Error deleting bot ${botId}:`, error);
    // Don't show additional notification here since apiClient already handles it
    throw error;
  }
};

// Wallet balances for authenticated user (optional: specific wallet from exchange section)
export const getWalletBalances = async (chainId: number, tokens?: string[], privateKeyId?: number) => {
  const payload: Record<string, unknown> = { chain_id: chainId };
  if (tokens && tokens.length > 0) {
    payload.tokens = tokens;
  }
  if (privateKeyId != null) {
    payload.private_key_id = privateKeyId;
  }
  return apiClient.post('/mtrades/wallet/balances', payload, { silent: true });
};

/**
 * Get trade history for a specific bot
 */
export const fetchBotTradeHistory = async (botId: number) => {
  try {
    const data = await apiClient.get(`/bots/${botId}/trades`);
    return data;
  } catch (error) {
    console.error(`Error fetching trade history for bot ${botId}:`, error);
    // Don't show additional notification here since apiClient already handles it
    throw error;
  }
};

/**
 * Fetch notifications from backend
 */
export const fetchNotifications = async (status?: string, limit = 50, offset = 0) => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    
    const data = await apiClient.get(`/notifications?${params.toString()}`);
    return data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async () => {
  try {
    const data = await apiClient.get('/notifications/unread-count');
    return data.count;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: number) => {
  try {
    await apiClient.patch(`/notifications/${notificationId}/read`, {});
    return true;
  } catch (error) {
    console.error(`Error marking notification ${notificationId} as read:`, error);
    return false;
  }
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async () => {
  try {
    const data = await apiClient.post('/notifications/mark-all-read', {});
    return data.updated || 0;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return 0;
  }
};

/**
 * Wallet management functions
 */
// Private key / wallet management functions
export const fetchPrivateKeys = async (keyType?: string): Promise<any[]> => {
  try {
    const query = keyType ? `?key_type=${keyType}` : '';
    return await apiClient.get(`/private-keys/${query}`);
  } catch (error) {
    console.error('Error fetching private keys:', error);
    throw error;
  }
};

export const createPrivateKey = async (data: { name: string; private_key: string; key_type: string }): Promise<any> => {
  try {
    const result = await apiClient.post('/private-keys/', data);
    showNotification({
      title: 'Wallet Added',
      message: `Wallet "${data.name}" has been added successfully`,
      color: 'green',
    });
    return result;
  } catch (error) {
    console.error('Error creating private key:', error);
    throw error;
  }
};

export const deletePrivateKey = async (keyId: number): Promise<any> => {
  try {
    const data = await apiClient.delete(`/private-keys/${keyId}`);
    showNotification({
      title: 'Wallet Deleted',
      message: 'Wallet has been deleted successfully',
      color: 'green',
    });
    return data;
  } catch (error) {
    console.error('Error deleting private key:', error);
    throw error;
  }
};

export const updatePrivateKey = async (keyId: number, data: { name: string }): Promise<any> => {
  try {
    const result = await apiClient.put(`/private-keys/${keyId}`, data);
    showNotification({
      title: 'Wallet Updated',
      message: 'Wallet name has been updated successfully',
      color: 'green',
    });
    return result;
  } catch (error) {
    console.error('Error updating private key:', error);
    throw error;
  }
};

export const setDefaultPrivateKey = async (keyId: number): Promise<any> => {
  try {
    const result = await apiClient.post(`/private-keys/${keyId}/set-default`, {});
    showNotification({
      title: 'Default Wallet Set',
      message: 'Default wallet has been updated',
      color: 'blue',
    });
    return result;
  } catch (error) {
    console.error('Error setting default private key:', error);
    throw error;
  }
};

export const getPrivateKeyStatus = async (): Promise<any> => {
  try {
    return await apiClient.get('/private-keys/status');
  } catch (error) {
    console.error('Error getting private key status:', error);
    throw error;
  }
};
